<template>
  <div class="c-header">
    <template v-for="status in statuses" :key="status.type + status.text">
      <div class="c-row">
        <span class="c-header-title">{{ status.name || 'Status' }}</span>
        <BittsBadge
          :status="status.type"
          class="c-header-badge"
          :text="status.text"
          data-testid="status-badge"
        />
      </div>
      <div
        v-if="status.lastPushed"
        class="c-row"
        data-testid="last-pushed-date"
      >
        <span class="c-header-title">Last pushed</span>
        <span class="text-neutral-text-strong">
          {{ formatDateTime(status.lastPushed) }}
        </span>
      </div>
    </template>
    <div v-if="version" class="c-row">
      <span class="c-header-title">Version</span>
      <BittsBadge class="c-header-badge no-icon" :text="`v${version}`" />
    </div>
    <div class="c-row" v-if="lastSyncedAt">
      <span class="c-header-title">Last synced</span>
      <span class="text-neutral-text-strong">{{ lastSyncedAt }}</span>
    </div>
    <div class="c-row" v-if="createdAt">
      <span class="c-header-title">Last authorized</span>
      <span class="text-neutral-text-strong">{{ createdAt }}</span>
    </div>
    <div v-if="authBy" class="c-row">
      <span class="c-header-title">Authorized by</span>
      <span class="text-neutral-text-strong">{{ authBy }}</span>
    </div>
    <BittsButton
      v-if="!hideReauth"
      v-bind="buttonOptions"
      data-testid="integration-reauth-button"
      text="Re-authorize"
      :left-icon="['fak', 'cycle']"
      @click="() => emits('reauthorize')"
    />
    <BittsButton
      v-if="!hideReauth && !hideAdvancedSettings"
      v-bind="buttonOptions"
      data-testid="integration-reauth-button"
      text="Advanced settings"
      :right-icon="['fak', 'external-link']"
      @click="() => emits('reauthorize')"
      variant="ghost"
    />
  </div>
  <BittsAlert
    v-if="calloutText"
    :description="errorType === 'scope_check_failed' ? '' : calloutText"
    :color="statusType"
    class="mt-16"
  >
    <template #body>
      <span class="text-danger-text"> {{ calloutText }} </span>&nbsp;<BittsLink
        :open-in-new-tab="true"
        text="Learn more"
        url="https://help.crossbeam.com/en/articles/7971155-hubspot-custom-object-integration#h_4fa86516b7"
      />
    </template>
  </BittsAlert>
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsBadge,
  BittsButton,
  BittsButtonProps,
  BittsLink,
} from '@crossbeam/bitts';

import { DateTime } from 'luxon';
import { computed } from 'vue';

import { IntegrationProfileStatus } from '@/types/integration_profiles';
import { TrayError, TrayIntegrationHeaderStatus } from '@/types/integrations';

const props = defineProps<{
  statusType?: IntegrationProfileStatus;
  statuses: TrayIntegrationHeaderStatus[];
  version?: string | null;
  created?: string | null;
  lastSynced?: string | null;
  authBy?: string | null;
  loading?: boolean;
  disabled?: boolean;
  calloutText?: string;
  hideReauth?: boolean;
  hideAdvancedSettings?: boolean;
  errorType?: TrayError;
}>();

const emits = defineEmits(['reauthorize']);

function formatDateTime(isoString?: string) {
  if (!isoString) return undefined;
  const dateTime = DateTime.fromISO(isoString);
  return dateTime.isValid
    ? dateTime.toFormat("LLL dd, yyyy 'at' h:mm a")
    : undefined;
}
const createdAt = computed(() => formatDateTime(props.created || ''));
const lastSyncedAt = computed(() => formatDateTime(props.lastSynced || ''));
const buttonOptions = computed<BittsButtonProps>(() => ({
  loading: props.loading,
  disabled: props.disabled,
  variant: 'outline',
  type: 'neutral',
  size: 'small',
}));
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.c-header {
  @apply flex items-center justify-between flex-wrap gap-y-12;
  & .c-row {
    @apply flex items-center justify-between w-full;
    & .c-header-title {
      @apply text-base leading-6 text-neutral-text-weak;
    }
    & .c-header-badge {
      @apply text-brand-navy inline-block;

      &.no-icon {
        & :deep(.ant-badge-status-dot) {
          @apply hidden;
        }
      }
    }
  }
}
</style>
